import * as React from 'react'
import { ChevronsUpDown, Check, X, Loader2 } from 'lucide-react'
import { useDebounce } from 'use-debounce'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export type SearchDropdownItem<T = any> = {
  id: string
  label: string
  sublabel?: string
  icon?: React.ReactNode
  data?: T
  isExclusive?: boolean
}

type SearchDropdownProps<T = any> = {
  items?: SearchDropdownItem<T>[]
  selectedItems?: SearchDropdownItem<T> | SearchDropdownItem<T>[] | null
  onSelect?: (
    item: SearchDropdownItem<T> | SearchDropdownItem<T>[] | null,
  ) => void | Promise<void>
  fetchItems?: (query?: string) => Promise<SearchDropdownItem<T>[]>
  loadInitialItems?: boolean
  loadInitialData?: boolean
  placeholder?: string
  searchPlaceholder?: string
  buttonLabel?: string
  buttonClassName?: string
  multiSelect?: boolean
  disabled?: boolean
  popoverClassName?: string
  popoverAlign?: 'start' | 'center' | 'end'
  popoverSide?: 'top' | 'right' | 'bottom' | 'left'
  icon?: React.ReactNode
  showChevron?: boolean
  renderItem?: (
    item: SearchDropdownItem<T>,
    isSelected: boolean,
  ) => React.ReactNode
  renderTrigger?: (
    selectedItems: SearchDropdownItem<T>[],
    isOpen: boolean,
  ) => React.ReactNode
  emptyMessage?: string
  loadingMessage?: string
  maxHeight?: string
  debounceMs?: number
}

export function SearchDropdown<T = any>({
  items: staticItems = [],
  selectedItems: initialSelectedItems,
  onSelect,
  fetchItems,
  loadInitialItems = true,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  buttonLabel,
  buttonClassName,
  multiSelect = false,
  disabled = false,
  popoverClassName,
  popoverAlign = 'start',
  popoverSide = 'bottom',
  icon,
  showChevron = true,
  renderItem,
  renderTrigger,
  emptyMessage = 'No items found',
  loadingMessage = 'Searching...',
  maxHeight = '256px',
  debounceMs = 300,
}: SearchDropdownProps<T>) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [apiItems, setApiItems] = React.useState<SearchDropdownItem<T>[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(-1)
  const [debouncedSearchQuery] = useDebounce(searchQuery, debounceMs)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const normalizeSelection = (
    selection:
      | SearchDropdownItem<T>
      | SearchDropdownItem<T>[]
      | null
      | undefined,
  ): SearchDropdownItem<T>[] => {
    if (!selection) return []
    return Array.isArray(selection) ? selection : [selection]
  }

  const [selectedItemsState, setSelectedItemsState] = React.useState<
    SearchDropdownItem<T>[]
  >(normalizeSelection(initialSelectedItems))

  React.useEffect(() => {
    setSelectedItemsState(normalizeSelection(initialSelectedItems))
  }, [initialSelectedItems])

  React.useEffect(() => {
    if (!fetchItems) return

    const performSearch = async () => {
      setIsLoading(true)
      try {
        const results = await fetchItems(debouncedSearchQuery)
        setApiItems(results)
      } catch (error) {
        console.error('Failed to fetch items:', error)
        setApiItems([])
      } finally {
        setIsLoading(false)
      }
    }

    if (loadInitialItems || debouncedSearchQuery) {
      performSearch()
    }
  }, [debouncedSearchQuery, fetchItems, loadInitialItems])

  const allItems = fetchItems ? apiItems : staticItems

  const filteredItems = React.useMemo(() => {
    if (fetchItems) {
      return allItems
    }

    const query = searchQuery.trim().toLowerCase()
    if (!query) return allItems

    return allItems.filter(item => {
      const label = (item.label ?? '').toLowerCase()
      const sublabel = (item.sublabel ?? '').toLowerCase()
      return label.includes(query) || (sublabel && sublabel.includes(query))
    })
  }, [allItems, searchQuery, fetchItems])

  const handleItemSelect = async (item: SearchDropdownItem<T>) => {
    let newSelection: SearchDropdownItem<T>[]

    if (multiSelect) {
      const isSelected = selectedItemsState.some(
        selected => selected.id === item.id,
      )

      // Check if there's currently an exclusive item selected
      const hasExclusiveSelected = selectedItemsState.some(s => s.isExclusive)

      if (isSelected) {
        // Deselecting an item
        newSelection = selectedItemsState.filter(
          selected => selected.id !== item.id,
        )
      } else if (item.isExclusive) {
        // Selecting an exclusive item - clear all others
        newSelection = [item]
      } else if (hasExclusiveSelected) {
        // Trying to select a regular item when exclusive is selected - replace exclusive with this item
        newSelection = [item]
      } else {
        // Normal multi-select behavior
        newSelection = [...selectedItemsState, item]
      }
    } else {
      newSelection = [item]
      setIsOpen(false)
    }

    setSelectedItemsState(newSelection)
    setSearchQuery('')
    setSelectedIndex(-1)

    if (onSelect) {
      await onSelect(multiSelect ? newSelection : newSelection[0] || null)
    }
  }

  const handleRemoveItem = async (
    itemToRemove: SearchDropdownItem<T>,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation()
    const newSelection = selectedItemsState.filter(
      item => item.id !== itemToRemove.id,
    )
    setSelectedItemsState(newSelection)

    if (onSelect) {
      await onSelect(multiSelect ? newSelection : null)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < filteredItems.length - 1 ? prev + 1 : prev,
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && filteredItems[selectedIndex]) {
          handleItemSelect(filteredItems[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        break
    }
  }

  const handlePopoverOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setSelectedIndex(-1)
      setSearchQuery('')
    }
  }

  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  React.useEffect(() => {
    setSelectedIndex(-1)
  }, [searchQuery])

  const defaultTrigger = (
    <Button
      variant="outline"
      className={cn('w-full justify-between h-auto min-h-9', buttonClassName)}
      role="combobox"
      aria-expanded={isOpen}
      aria-label={placeholder}
      disabled={disabled}
    >
      <div className="flex items-center min-w-0 font-normal">
        {multiSelect ? (
          selectedItemsState.length > 0 ? (
            <div className="flex flex-wrap gap-1 text-left">
              {selectedItemsState.map(item => (
                <Badge key={item.id} variant="secondary" className="text-xs">
                  {item.label}
                  <button
                    onClick={e => handleRemoveItem(item, e)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )
        ) : (
          <>
            {icon}
            <span
              className={cn(
                'truncate',
                icon && 'ml-2',
                !selectedItemsState[0] && 'text-muted-foreground',
              )}
            >
              {buttonLabel || selectedItemsState[0]?.label || placeholder}
            </span>
          </>
        )}
      </div>
      {showChevron && (
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      )}
    </Button>
  )

  const trigger = renderTrigger
    ? renderTrigger(selectedItemsState, isOpen)
    : defaultTrigger

  const defaultItemRender = (
    item: SearchDropdownItem<T>,
    isSelected: boolean,
  ) => (
    <>
      {item.icon && <div className="shrink-0">{item.icon}</div>}
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{item.label}</div>
        {item.sublabel && (
          <div className="text-xs text-muted-foreground truncate">
            {item.sublabel}
          </div>
        )}
      </div>
      {isSelected && <Check className="h-4 w-4 shrink-0" />}
    </>
  )

  return (
    <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className={cn(
          'p-0 overflow-hidden',
          popoverClassName || 'w-[--radix-popover-trigger-width]',
        )}
        align={popoverAlign}
        side={popoverSide}
      >
        <div>
          <Input
            className="px-4"
            ref={searchInputRef}
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            variant="popover"
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
          />
        </div>

        <div className="overflow-y-auto pt-1" style={{ maxHeight }}>
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingMessage}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            filteredItems.map((item, index) => {
              const isSelected = selectedItemsState.some(
                selected => selected.id === item.id,
              )

              return (
                <div
                  key={item.id}
                  onClick={() => handleItemSelect(item)}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 mx-1.5 my-1.5 text-sm transition-colors rounded-md cursor-pointer hover:bg-accent',
                    isSelected && 'bg-accent',
                    selectedIndex === index && 'bg-accent',
                  )}
                >
                  {renderItem
                    ? renderItem(item, isSelected)
                    : defaultItemRender(item, isSelected)}
                </div>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
